import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Cpu, Database, GitBranch, AlertTriangle } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import usePageMeta from '../utils/usePageMeta';

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Post-Match Analysis Engine | Tactmark",
  "description": "The Tactmark Analysis Engine is a post-match analysis engine that standardizes how football clubs turn match evidence into structured coaching decisions. Post-game analysis without video hardware.",
  "url": "https://tactmark.com/post-match-analysis-engine",
  "mainEntity": {
    "@type": "SoftwareApplication",
    "name": "Tactmark Analysis Engine",
    "applicationCategory": "SportsApplication",
    "description": "Post-match analysis engine for football and soccer coaching staffs. Standardizes evidence collection, applies consistent tactical evaluation, and produces traceable coaching decisions."
  }
};

const ENGINE_STEPS = [
  { icon: <Database className="w-5 h-5" />, title: 'Evidence Collection', body: 'Structured capture of key moments, score-state, phase of play, team unit, role observations, and coach observations. Works from notes, XLSX import, or direct paste.' },
  { icon: <Cpu className="w-5 h-5" />, title: 'Tactical Evaluation', body: 'The Analysis Engine applies repeatability, detectability, and team impact scoring based strictly on tactical and structural criteria, not generic templated output.' },
  { icon: <GitBranch className="w-5 h-5" />, title: 'Pattern Detection', body: 'Compare findings across matches to identify structural patterns your squad repeats at set pieces, in transition, under defensive pressure, or in specific score states.' },
  { icon: <AlertTriangle className="w-5 h-5" />, title: 'Evidence Provenance', body: 'Every finding carries a source, status (corroborated, conflicting, single-observation), confidence rating, and staff review instruction so coaching decisions are accountable.' },
];

const FAQS = [
  { q: "What is the Tactmark Analysis Engine?", a: "The Tactmark Analysis Engine is a post-match analysis engine that applies a consistent tactical evaluation framework to every match a coaching staff records. It produces findings with evidence provenance, confidence ratings, and staff review instructions standardizing the quality of coaching decisions across a football organization." },
  { q: "How is a post-match analysis engine different from a report template?", a: "A report template defines fields. A post-match analysis engine enforces those fields, evaluates the evidence, detects patterns across matches, and produces a structured decision record automatically." },
  { q: "Does the Tactmark post-game analysis engine use video?", a: "No. The Tactmark post-game analysis engine is built on structured coach observation. Footage references can be added as optional evidence, but a complete, professional-grade coaching report can be generated from observations alone." },
  { q: "Can the post-match analysis engine be used with AI tools like ChatGPT or Claude?", a: "Yes. The Tactmark Analysis Engine produces structured, exportable evidence records that AI tools including ChatGPT, Claude, and Perplexity can analyze more reliably than unstructured notes or raw video transcripts." },
  { q: "Does Tactmark's post-game analysis engine work for soccer as well as football?", a: "Yes. The Tactmark Analysis Engine is used by both football clubs and soccer organizations. The same engine applies regardless of whether your organization uses 'football' or 'soccer' terminology." },
  { q: "How long does the post-game analysis engine take to generate a report?", a: "A complete coaching report can be generated in under five minutes from match notes. The three-step workflow Match Details, Key Moments, Generate Report is designed to be completed by the head coach on the way home from the match." },
];

export default function PostMatchAnalysisEngine() {
  usePageMeta(
    'Post-Match Analysis Engine for Football | Tactmark',
    'The Tactmark Analysis Engine is a post-match analysis engine that standardizes football match evidence into coaching decisions. Post-game analysis engine for soccer and football clubs.'
  );

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(SCHEMA) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage",
        "mainEntity": FAQS.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } }))
      })}} />
      <PublicNav />
      <main className="bg-white">
        <section className="bg-navy-900 text-white py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-navy-300 text-xs font-semibold tracking-widest uppercase mb-4">Post-Match Analysis Engine · Post-Game Analysis · Football Coaching Intelligence</p>
            <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-6">
              The Post-Match Analysis Engine That Turns Match Evidence Into Coaching Decisions
            </h1>
            <p className="text-navy-200 text-lg leading-relaxed max-w-3xl mx-auto mb-8">
              The Tactmark Analysis Engine applies a consistent tactical evaluation framework to every football match your coaching staff records producing findings that are comparable, traceable, and actionable. No video. No hardware. No analyst required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/login" className="bg-white text-navy-900 font-semibold px-8 py-3.5 rounded-xl hover:bg-navy-50 transition-colors">Try the engine free</Link>
              <Link to="/how-it-works" className="border border-navy-400 text-white px-8 py-3.5 rounded-xl hover:border-white transition-colors font-semibold">How it works</Link>
            </div>
          </div>
        </section>

        {/* How the engine works */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-navy-900 text-center mb-4">How the Post-Match Analysis Engine Works</h2>
            <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">The Tactmark Analysis Engine is a four-layer system that turns raw match observations into structured, accountable coaching decisions.</p>
            <div className="grid sm:grid-cols-2 gap-6">
              {ENGINE_STEPS.map(({ icon, title, body }, i) => (
                <div key={title} className="bg-gray-50 rounded-2xl p-6 relative overflow-hidden">
                  <div className="absolute top-4 right-4 text-gray-100 font-bold text-5xl leading-none select-none">{i + 1}</div>
                  <div className="bg-navy-900 text-white rounded-lg w-9 h-9 flex items-center justify-center mb-4 relative z-10">{icon}</div>
                  <h3 className="text-navy-900 font-semibold text-lg mb-2 relative z-10">{title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed relative z-10">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Engine output */}
        <section className="bg-gray-50 py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-navy-900 text-center mb-4">What the Post-Game Analysis Engine Produces</h2>
            <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">Every report generated by the Tactmark Analysis Engine includes structured findings with full evidence accountability.</p>
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="bg-navy-900 px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="text-navy-300 text-xs font-semibold tracking-widest uppercase">Tactmark Analysis Engine</p>
                  <p className="text-white text-sm font-medium mt-0.5">Riverside FC vs Hartley United · Match Report</p>
                </div>
                <div className="text-right">
                  <p className="text-white text-2xl font-bold leading-none">74</p>
                  <p className="text-navy-300 text-xs mt-0.5">/ 100</p>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="border border-red-200 bg-red-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                    <span className="text-red-700 text-xs font-semibold">Critical Risk · 71' · High-line Exposure</span>
                    <span className="ml-auto text-xs text-gray-500 bg-gray-100 rounded px-2 py-0.5">Confidence: High · Source: Coach observation</span>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-2">Right back stepped high without defensive cover conceding the counter-attack goal in a critical score-state moment.</p>
                  <p className="text-xs text-gray-500 font-medium">Pattern status: Active · 3 matches · Staff review: Defensive shape under pressure</p>
                </div>
                <div className="border border-amber-200 bg-amber-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-amber-700 text-xs font-semibold">Watchlist · Set-Piece Defensive Organization</span>
                    <span className="ml-auto text-xs text-gray-500 bg-gray-100 rounded px-2 py-0.5">Confidence: Moderate · Status: New</span>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">Second corner conceded without assigned markers. Requires further review before confirmed as a structural pattern.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* vs alternatives */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-navy-900 text-center mb-10">Post-Match Analysis Engine vs Alternatives</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 text-gray-500 font-medium w-1/3"></th>
                    <th className="text-center py-3 px-4 text-navy-900 font-semibold">Tactmark Engine</th>
                    <th className="text-center py-3 px-4 text-gray-500 font-medium">Video Software</th>
                    <th className="text-center py-3 px-4 text-gray-500 font-medium">Spreadsheets</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['No hardware required', '✓', '✗', '✓'],
                    ['Consistent report structure', '✓', '', '✗'],
                    ['Pattern detection across matches', '✓', '', '✗'],
                    ['Evidence provenance tracking', '✓', '✗', '✗'],
                    ['Organization-wide sharing', '✓', '', '✗'],
                    ['Coach can complete independently', '✓', '✗', '✓'],
                    ['AI-tool compatible output', '✓', '', ''],
                    ['Report in under 5 minutes', '✓', '✗', ''],
                  ].map(([feature, a, b, c]) => (
                    <tr key={feature} className="border-b border-gray-100">
                      <td className="py-3 px-4 text-gray-700">{feature}</td>
                      <td className="py-3 px-4 text-center font-semibold text-green-600">{a}</td>
                      <td className="py-3 px-4 text-center text-gray-400">{b}</td>
                      <td className="py-3 px-4 text-center text-gray-400">{c}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Related articles */}
        <section className="bg-gray-50 py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-navy-900 mb-8">Post-Match Analysis Engine Guides</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { slug: 'what-is-a-post-match-analysis-engine-in-football', label: 'What Is a Post-Match Analysis Engine in Football?' },
                { slug: 'post-match-analysis-engine-vs-video-analysis-software', label: 'Post-Match Analysis Engine vs Video Analysis Software' },
                { slug: 'how-a-post-match-analysis-engine-improves-coaching-consistency', label: 'How a Post-Match Analysis Engine Improves Coaching Consistency' },
                { slug: 'football-post-match-analysis-engine-for-academy-clubs', label: 'Post-Match Analysis Engine for Academy Clubs' },
                { slug: 'post-game-analysis-engine-for-football-clubs', label: 'What a Post-Game Analysis Engine Does for a Football Club' },
                { slug: 'how-ai-tools-are-changing-football-post-match-analysis', label: 'How AI Tools Are Changing Football Post-Match Analysis' },
              ].map(({ slug, label }) => (
                <Link key={slug} to={`/insights/${slug}`}
                  className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center justify-between hover:border-navy-400 transition-colors group">
                  <span className="text-navy-900 text-sm font-medium leading-snug">{label}</span>
                  <ArrowRight className="w-4 h-4 text-gray-400 shrink-0 ml-3 group-hover:text-navy-600" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-navy-900 mb-8">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {FAQS.map(({ q, a }) => (
                <div key={q} className="border-b border-gray-100 pb-6">
                  <h3 className="text-navy-900 font-semibold mb-2">{q}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-navy-900 py-16 px-6 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-4">Generate Your First Engine Report</h2>
            <p className="text-navy-200 mb-8">Match Details → Key Moments → Generate Report. Under five minutes. No hardware required.</p>
            <Link to="/login" className="bg-white text-navy-900 font-semibold px-8 py-3.5 rounded-xl hover:bg-navy-50 transition-colors text-base inline-block">Start free</Link>
          </div>
        </section>
      </main>
      <PublicFooter />
    </>
  );
}
